import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { PersonApi } from "../../api/PersonApi";
import type { AddressStaff } from "../../generated/dto/AddressStaff";
import type { AgreementFetchOptions } from "../../generated/dto/AgreementFetchOptions";
import type { AgreementInstanceFetchOptions } from "../../generated/dto/AgreementInstanceFetchOptions";
import type { AgreementInstanceStaff } from "../../generated/dto/AgreementInstanceStaff";
import type { AgreementStaff } from "../../generated/dto/AgreementStaff";
import type { ApClassFetchOptions } from "../../generated/dto/ApClassFetchOptions";
import type { ApClassSignupStaff } from "../../generated/dto/ApClassSignupStaff";
import type { ApClassSignupStaffFetchOptions } from "../../generated/dto/ApClassSignupStaffFetchOptions";
import type { ApClassStaff } from "../../generated/dto/ApClassStaff";
import type { ApClassTypeOverrideFetchOptions } from "../../generated/dto/ApClassTypeOverrideFetchOptions";
import type { ApClassTypeOverrideStaff } from "../../generated/dto/ApClassTypeOverrideStaff";
import type { AssessmentStaff } from "../../generated/dto/AssessmentStaff";
import type { BoatTypeStaff } from "../../generated/dto/BoatTypeStaff";
import type { CardFetchOptions } from "../../generated/dto/CardFetchOptions";
import type { CardStaff } from "../../generated/dto/CardStaff";
import type { CreatePersonAddress } from "../../generated/dto/CreatePersonAddress";
import type { CreatePersonMember } from "../../generated/dto/CreatePersonMember";
import type { CreatePersonPersonType } from "../../generated/dto/CreatePersonPersonType";
import type { CreatePersonPhone } from "../../generated/dto/CreatePersonPhone";
import type { CreatePersonReferral } from "../../generated/dto/CreatePersonReferral";
import type { CreatePersonRelation } from "../../generated/dto/CreatePersonRelation";
import type { CreatePersonStaff } from "../../generated/dto/CreatePersonStaff";
import type { DamageWaiverFetchOptions } from "../../generated/dto/DamageWaiverFetchOptions";
import type { DamageWaiverStaff } from "../../generated/dto/DamageWaiverStaff";
import type { DeletePersonAddressStaff } from "../../generated/dto/DeletePersonAddressStaff";
import type { DeletePersonPersonTypeStaff } from "../../generated/dto/DeletePersonPersonTypeStaff";
import type { DeletePersonPhoneStaff } from "../../generated/dto/DeletePersonPhoneStaff";
import type { DeletePersonReferralStaff } from "../../generated/dto/DeletePersonReferralStaff";
import type { DeletePersonRelationStaff } from "../../generated/dto/DeletePersonRelationStaff";
import type { DeletePersonStaff } from "../../generated/dto/DeletePersonStaff";
import type { JpClassFetchOptions } from "../../generated/dto/JpClassFetchOptions";
import type { JpClassSignupFetchOptions } from "../../generated/dto/JpClassSignupFetchOptions";
import type { JpClassSignupStaff } from "../../generated/dto/JpClassSignupStaff";
import type { JpClassStaff } from "../../generated/dto/JpClassStaff";
import type { JpClassTypeOverrideFetchOptions } from "../../generated/dto/JpClassTypeOverrideFetchOptions";
import type { JpClassTypeOverrideStaff } from "../../generated/dto/JpClassTypeOverrideStaff";
import type { MemberProfile } from "../../generated/dto/MemberProfile";
import type { MemberProfileCreate } from "../../generated/dto/MemberProfileCreate";
import type { MemberProfileUpdate } from "../../generated/dto/MemberProfileUpdate";
import type { MembershipMember } from "../../generated/dto/MembershipMember";
import type { MembershipMemberFetchOptions } from "../../generated/dto/MembershipMemberFetchOptions";
import type { MembershipStaff } from "../../generated/dto/MembershipStaff";
import type { MembershipStaffFetchOptions } from "../../generated/dto/MembershipStaffFetchOptions";
import type { PersonAddressFetchOptions } from "../../generated/dto/PersonAddressFetchOptions";
import type { PersonAddressSearchParams } from "../../generated/dto/PersonAddressSearchParams";
import type { PersonAddressStaff } from "../../generated/dto/PersonAddressStaff";
import type { PersonAddressStaffSearchResult } from "../../generated/dto/PersonAddressStaffSearchResult";
import type { PersonAgreementInstanceFetchOptions } from "../../generated/dto/PersonAgreementInstanceFetchOptions";
import type { PersonAgreementInstanceSearchParams } from "../../generated/dto/PersonAgreementInstanceSearchParams";
import type { PersonAgreementInstanceStaff } from "../../generated/dto/PersonAgreementInstanceStaff";
import type { PersonAgreementInstanceStaffSearchResult } from "../../generated/dto/PersonAgreementInstanceStaffSearchResult";
import type { PersonAgreementsPage } from "../../generated/dto/PersonAgreementsPage";
import type { PersonCheckInFetchOptions } from "../../generated/dto/PersonCheckInFetchOptions";
import type { PersonCheckInStaff } from "../../generated/dto/PersonCheckInStaff";
import type { PersonCommentFetchOptions } from "../../generated/dto/PersonCommentFetchOptions";
import type { PersonCommentSearchParams } from "../../generated/dto/PersonCommentSearchParams";
import type { PersonCommentStaff } from "../../generated/dto/PersonCommentStaff";
import type { PersonCommentStaffSearchResult } from "../../generated/dto/PersonCommentStaffSearchResult";
import type { PersonCreateEmergencyContactRequest } from "../../generated/dto/PersonCreateEmergencyContactRequest";
import type { PersonFetchOptions } from "../../generated/dto/PersonFetchOptions";
import type { PersonGetPersonNameRequest } from "../../generated/dto/PersonGetPersonNameRequest";
import type { PersonGrantRatingRequest } from "../../generated/dto/PersonGrantRatingRequest";
import type { PersonMember } from "../../generated/dto/PersonMember";
import type { PersonMemberFetchOptions } from "../../generated/dto/PersonMemberFetchOptions";
import type { PersonMemberSearchParams } from "../../generated/dto/PersonMemberSearchParams";
import type { PersonMemberSearchResult } from "../../generated/dto/PersonMemberSearchResult";
import type { PersonPersonTypeFetchOptions } from "../../generated/dto/PersonPersonTypeFetchOptions";
import type { PersonPersonTypeStaff } from "../../generated/dto/PersonPersonTypeStaff";
import type { PersonPhoneFetchOptions } from "../../generated/dto/PersonPhoneFetchOptions";
import type { PersonPhoneSearchParams } from "../../generated/dto/PersonPhoneSearchParams";
import type { PersonPhoneStaff } from "../../generated/dto/PersonPhoneStaff";
import type { PersonPhoneStaffSearchResult } from "../../generated/dto/PersonPhoneStaffSearchResult";
import type { PersonRatingFetchOptions } from "../../generated/dto/PersonRatingFetchOptions";
import type { PersonRatingStaff } from "../../generated/dto/PersonRatingStaff";
import type { PersonRef } from "../../generated/dto/PersonRef";
import type { PersonReferralSearchParams } from "../../generated/dto/PersonReferralSearchParams";
import type { PersonReferralStaff } from "../../generated/dto/PersonReferralStaff";
import type { PersonReferralStaffSearchResult } from "../../generated/dto/PersonReferralStaffSearchResult";
import type { PersonRelationFetchOptions } from "../../generated/dto/PersonRelationFetchOptions";
import type { PersonRelationRef } from "../../generated/dto/PersonRelationRef";
import type { PersonRelationSearchParams } from "../../generated/dto/PersonRelationSearchParams";
import type { PersonRelationStaff } from "../../generated/dto/PersonRelationStaff";
import type { PersonRelationStaffSearchResult } from "../../generated/dto/PersonRelationStaffSearchResult";
import type { PersonRelationTypeStaff } from "../../generated/dto/PersonRelationTypeStaff";
import type { PersonRemoveCommentRequest } from "../../generated/dto/PersonRemoveCommentRequest";
import type { PersonRemovePersonAddressRequest } from "../../generated/dto/PersonRemovePersonAddressRequest";
import type { PersonRemovePersonPhoneRequest } from "../../generated/dto/PersonRemovePersonPhoneRequest";
import type { PersonRemoveSkillRequest } from "../../generated/dto/PersonRemoveSkillRequest";
import type { PersonRescindRatingRequest } from "../../generated/dto/PersonRescindRatingRequest";
import type { PersonSaveCommentRequest } from "../../generated/dto/PersonSaveCommentRequest";
import type { PersonSaveSkillRequest } from "../../generated/dto/PersonSaveSkillRequest";
import type { PersonSearchPersonAgreementsRequest } from "../../generated/dto/PersonSearchPersonAgreementsRequest";
import type { PersonSetAdminHoldRequest } from "../../generated/dto/PersonSetAdminHoldRequest";
import type { PersonSetTagsRequest } from "../../generated/dto/PersonSetTagsRequest";
import type { PersonSignAgreementRequest } from "../../generated/dto/PersonSignAgreementRequest";
import type { PersonSkillChangeFetchOptions } from "../../generated/dto/PersonSkillChangeFetchOptions";
import type { PersonSkillChangeSearchParams } from "../../generated/dto/PersonSkillChangeSearchParams";
import type { PersonSkillChangeStaff } from "../../generated/dto/PersonSkillChangeStaff";
import type { PersonSkillChangeStaffSearchResult } from "../../generated/dto/PersonSkillChangeStaffSearchResult";
import type { PersonSkillFetchOptions } from "../../generated/dto/PersonSkillFetchOptions";
import type { PersonSkillSearchParams } from "../../generated/dto/PersonSkillSearchParams";
import type { PersonSkillStaff } from "../../generated/dto/PersonSkillStaff";
import type { PersonSkillStaffSearchResult } from "../../generated/dto/PersonSkillStaffSearchResult";
import type { PersonStaff } from "../../generated/dto/PersonStaff";
import type { PersonStaffSearchParams } from "../../generated/dto/PersonStaffSearchParams";
import type { PersonStaffSearchResult } from "../../generated/dto/PersonStaffSearchResult";
import type { PersonTagFetchOptions } from "../../generated/dto/PersonTagFetchOptions";
import type { PersonTagSearchParams } from "../../generated/dto/PersonTagSearchParams";
import type { PersonTagStaff } from "../../generated/dto/PersonTagStaff";
import type { PersonTagStaffSearchResult } from "../../generated/dto/PersonTagStaffSearchResult";
import type { PersonTypeStaff } from "../../generated/dto/PersonTypeStaff";
import type { PhoneStaff } from "../../generated/dto/PhoneStaff";
import type { ProgramTypeStaff } from "../../generated/dto/ProgramTypeStaff";
import type { RatingChangeFetchOptions } from "../../generated/dto/RatingChangeFetchOptions";
import type { RatingChangeSearchParams } from "../../generated/dto/RatingChangeSearchParams";
import type { RatingChangeStaff } from "../../generated/dto/RatingChangeStaff";
import type { RatingChangeStaffSearchResult } from "../../generated/dto/RatingChangeStaffSearchResult";
import type { RatingFetchOptions } from "../../generated/dto/RatingFetchOptions";
import type { RatingStaff } from "../../generated/dto/RatingStaff";
import type { SavePersonAddress } from "../../generated/dto/SavePersonAddress";
import type { SavePersonPhone } from "../../generated/dto/SavePersonPhone";
import type { SignoutCrewFetchOptions } from "../../generated/dto/SignoutCrewFetchOptions";
import type { SignoutCrewSearchParams } from "../../generated/dto/SignoutCrewSearchParams";
import type { SignoutCrewStaff } from "../../generated/dto/SignoutCrewStaff";
import type { SignoutCrewStaffSearchResult } from "../../generated/dto/SignoutCrewStaffSearchResult";
import type { SignoutFetchOptions } from "../../generated/dto/SignoutFetchOptions";
import type { SignoutMember } from "../../generated/dto/SignoutMember";
import type { SignoutMemberFetchOptions } from "../../generated/dto/SignoutMemberFetchOptions";
import type { SignoutMemberSearchParams } from "../../generated/dto/SignoutMemberSearchParams";
import type { SignoutMemberSearchResult } from "../../generated/dto/SignoutMemberSearchResult";
import type { SignoutSearchParams } from "../../generated/dto/SignoutSearchParams";
import type { SignoutStaff } from "../../generated/dto/SignoutStaff";
import type { SignoutStaffSearchResult } from "../../generated/dto/SignoutStaffSearchResult";
import type { SkillAssessmentFetchOptions } from "../../generated/dto/SkillAssessmentFetchOptions";
import type { SkillAssessmentStaff } from "../../generated/dto/SkillAssessmentStaff";
import type { SkillSearchParams } from "../../generated/dto/SkillSearchParams";
import type { SkillStaff } from "../../generated/dto/SkillStaff";
import type { SkillStaffSearchResult } from "../../generated/dto/SkillStaffSearchResult";
import type { StaffProfileCreateRequest } from "../../generated/dto/StaffProfileCreateRequest";
import type { StaffProfileUpdateRequest } from "../../generated/dto/StaffProfileUpdateRequest";
import type { TagStaff } from "../../generated/dto/TagStaff";
import type { UpdatePersonAddress } from "../../generated/dto/UpdatePersonAddress";
import type { UpdatePersonMember } from "../../generated/dto/UpdatePersonMember";
import type { UpdatePersonPersonType } from "../../generated/dto/UpdatePersonPersonType";
import type { UpdatePersonPhone } from "../../generated/dto/UpdatePersonPhone";
import type { UpdatePersonReferral } from "../../generated/dto/UpdatePersonReferral";
import type { UpdatePersonRelation } from "../../generated/dto/UpdatePersonRelation";
import type { UpdatePersonStaff } from "../../generated/dto/UpdatePersonStaff";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class PersonApiClient implements PersonApi {
  constructor(private readonly client = useClient()) {}

  async getPersonMember(id: number, options?: PersonMemberFetchOptions): Promise<PersonMember> {
    const { data } = await this.client.get<unknown>(`/person/member/${id}`, { params: serializeCbiDto(options, "generated.dto.PersonMemberFetchOptions") });
    return deserializeCbiDto<PersonMember>(data, "generated.dto.PersonMember");
  }

  async getPersonStaff(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    const { data } = await this.client.get<unknown>(`/person/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff>(data, "generated.dto.PersonStaff");
  }

  async getPersonPersonTypeStaff(id: number, options?: PersonPersonTypeFetchOptions): Promise<PersonPersonTypeStaff> {
    const { data } = await this.client.get<unknown>(`/personpersontype/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.PersonPersonTypeFetchOptions") });
    return deserializeCbiDto<PersonPersonTypeStaff>(data, "generated.dto.PersonPersonTypeStaff");
  }

  async getPersonRelationStaff(id: number, options?: PersonRelationFetchOptions): Promise<PersonRelationStaff> {
    const { data } = await this.client.get<unknown>(`/personrelation/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.PersonRelationFetchOptions") });
    return deserializeCbiDto<PersonRelationStaff>(data, "generated.dto.PersonRelationStaff");
  }

  async getPersonRelationTypeStaff(id: number): Promise<PersonRelationTypeStaff> {
    const { data } = await this.client.get<unknown>(`/personrelationtype/staff/${id}`);
    return deserializeCbiDto<PersonRelationTypeStaff>(data, "generated.dto.PersonRelationTypeStaff");
  }

  async getPersonReferralStaff(id: number): Promise<PersonReferralStaff> {
    const { data } = await this.client.get<unknown>(`/personreferral/staff/${id}`);
    return deserializeCbiDto<PersonReferralStaff>(data, "generated.dto.PersonReferralStaff");
  }

  async getPersonAddressStaff(id: number, options?: PersonAddressFetchOptions): Promise<PersonAddressStaff> {
    const { data } = await this.client.get<unknown>(`/personaddress/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.PersonAddressFetchOptions") });
    return deserializeCbiDto<PersonAddressStaff>(data, "generated.dto.PersonAddressStaff");
  }

  async getPersonPhoneStaff(id: number, options?: PersonPhoneFetchOptions): Promise<PersonPhoneStaff> {
    const { data } = await this.client.get<unknown>(`/personphone/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.PersonPhoneFetchOptions") });
    return deserializeCbiDto<PersonPhoneStaff>(data, "generated.dto.PersonPhoneStaff");
  }

  async getPersonCommentStaff(id: number, options?: PersonCommentFetchOptions): Promise<PersonCommentStaff> {
    const { data } = await this.client.get<unknown>(`/personcomment/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.PersonCommentFetchOptions") });
    return deserializeCbiDto<PersonCommentStaff>(data, "generated.dto.PersonCommentStaff");
  }

  async getPersonTagStaff(id: number, options?: PersonTagFetchOptions): Promise<PersonTagStaff> {
    const { data } = await this.client.get<unknown>(`/persontag/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.PersonTagFetchOptions") });
    return deserializeCbiDto<PersonTagStaff>(data, "generated.dto.PersonTagStaff");
  }

  async getSkillStaff(id: number): Promise<SkillStaff> {
    const { data } = await this.client.get<unknown>(`/skill/staff/${id}`);
    return deserializeCbiDto<SkillStaff>(data, "generated.dto.SkillStaff");
  }

  async getAssessmentStaff(id: number): Promise<AssessmentStaff> {
    const { data } = await this.client.get<unknown>(`/assessment/staff/${id}`);
    return deserializeCbiDto<AssessmentStaff>(data, "generated.dto.AssessmentStaff");
  }

  async getSkillAssessmentStaff(id: number, options?: SkillAssessmentFetchOptions): Promise<SkillAssessmentStaff> {
    const { data } = await this.client.get<unknown>(`/skillassessment/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.SkillAssessmentFetchOptions") });
    return deserializeCbiDto<SkillAssessmentStaff>(data, "generated.dto.SkillAssessmentStaff");
  }

  async getPersonSkillStaff(id: number, options?: PersonSkillFetchOptions): Promise<PersonSkillStaff> {
    const { data } = await this.client.get<unknown>(`/personskill/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.PersonSkillFetchOptions") });
    return deserializeCbiDto<PersonSkillStaff>(data, "generated.dto.PersonSkillStaff");
  }

  async getPersonSkillChangeStaff(id: number, options?: PersonSkillChangeFetchOptions): Promise<PersonSkillChangeStaff> {
    const { data } = await this.client.get<unknown>(`/personskillchange/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.PersonSkillChangeFetchOptions") });
    return deserializeCbiDto<PersonSkillChangeStaff>(data, "generated.dto.PersonSkillChangeStaff");
  }

  async getRatingChangeStaff(id: number, options?: RatingChangeFetchOptions): Promise<RatingChangeStaff> {
    const { data } = await this.client.get<unknown>(`/ratingchange/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.RatingChangeFetchOptions") });
    return deserializeCbiDto<RatingChangeStaff>(data, "generated.dto.RatingChangeStaff");
  }

  async getPersonAgreementInstanceStaff(id: number, options?: PersonAgreementInstanceFetchOptions): Promise<PersonAgreementInstanceStaff> {
    const { data } = await this.client.get<unknown>(`/personagreementinstance/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.PersonAgreementInstanceFetchOptions") });
    return deserializeCbiDto<PersonAgreementInstanceStaff>(data, "generated.dto.PersonAgreementInstanceStaff");
  }

  async getSignoutMember(id: number, options?: SignoutMemberFetchOptions): Promise<SignoutMember> {
    const { data } = await this.client.get<unknown>(`/signout/member/${id}`, { params: serializeCbiDto(options, "generated.dto.SignoutMemberFetchOptions") });
    return deserializeCbiDto<SignoutMember>(data, "generated.dto.SignoutMember");
  }

  async getSignoutStaff(id: number, options?: SignoutFetchOptions): Promise<SignoutStaff> {
    const { data } = await this.client.get<unknown>(`/signout/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.SignoutFetchOptions") });
    return deserializeCbiDto<SignoutStaff>(data, "generated.dto.SignoutStaff");
  }

  async getSignoutCrewStaff(id: number, options?: SignoutCrewFetchOptions): Promise<SignoutCrewStaff> {
    const { data } = await this.client.get<unknown>(`/signoutcrew/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.SignoutCrewFetchOptions") });
    return deserializeCbiDto<SignoutCrewStaff>(data, "generated.dto.SignoutCrewStaff");
  }

  async getAllPersonRelationTypeStaffs(): Promise<Array<PersonRelationTypeStaff>> {
    const { data } = await this.client.get<unknown>(`/personrelationtype/staff/all`);
    return deserializeCbiDto<Array<PersonRelationTypeStaff>>(data, "generated.dto.PersonRelationTypeStaff");
  }

  async getAllSkillStaffs(): Promise<Array<SkillStaff>> {
    const { data } = await this.client.get<unknown>(`/skill/staff/all`);
    return deserializeCbiDto<Array<SkillStaff>>(data, "generated.dto.SkillStaff");
  }

  async getAllAssessmentStaffs(): Promise<Array<AssessmentStaff>> {
    const { data } = await this.client.get<unknown>(`/assessment/staff/all`);
    return deserializeCbiDto<Array<AssessmentStaff>>(data, "generated.dto.AssessmentStaff");
  }

  async getAllSkillAssessmentStaffs(options?: SkillAssessmentFetchOptions): Promise<Array<SkillAssessmentStaff>> {
    const { data } = await this.client.get<unknown>(`/skillassessment/staff/all`, { params: serializeCbiDto(options, "generated.dto.SkillAssessmentFetchOptions") });
    return deserializeCbiDto<Array<SkillAssessmentStaff>>(data, "generated.dto.SkillAssessmentStaff");
  }

  async createPersonMember(source: CreatePersonMember): Promise<PersonMember> {
    const { data } = await this.client.post<unknown>(`/person/member`, serializeCbiDto(source, "generated.dto.CreatePersonMember"));
    return deserializeCbiDto<PersonMember>(data, "generated.dto.PersonMember");
  }

  async createPersonStaff(source: CreatePersonStaff): Promise<PersonStaff> {
    const { data } = await this.client.post<unknown>(`/person/staff`, serializeCbiDto(source, "generated.dto.CreatePersonStaff"));
    return deserializeCbiDto<PersonStaff>(data, "generated.dto.PersonStaff");
  }

  async createPersonPersonTypeStaff(source: CreatePersonPersonType): Promise<PersonPersonTypeStaff> {
    const { data } = await this.client.post<unknown>(`/personpersontype/staff`, serializeCbiDto(source, "generated.dto.CreatePersonPersonType"));
    return deserializeCbiDto<PersonPersonTypeStaff>(data, "generated.dto.PersonPersonTypeStaff");
  }

  async createPersonRelationStaff(source: CreatePersonRelation): Promise<PersonRelationStaff> {
    const { data } = await this.client.post<unknown>(`/personrelation/staff`, serializeCbiDto(source, "generated.dto.CreatePersonRelation"));
    return deserializeCbiDto<PersonRelationStaff>(data, "generated.dto.PersonRelationStaff");
  }

  async createPersonReferralStaff(source: CreatePersonReferral): Promise<PersonReferralStaff> {
    const { data } = await this.client.post<unknown>(`/personreferral/staff`, serializeCbiDto(source, "generated.dto.CreatePersonReferral"));
    return deserializeCbiDto<PersonReferralStaff>(data, "generated.dto.PersonReferralStaff");
  }

  async createPersonAddressStaff(source: CreatePersonAddress): Promise<PersonAddressStaff> {
    const { data } = await this.client.post<unknown>(`/personaddress/staff`, serializeCbiDto(source, "generated.dto.CreatePersonAddress"));
    return deserializeCbiDto<PersonAddressStaff>(data, "generated.dto.PersonAddressStaff");
  }

  async createPersonPhoneStaff(source: CreatePersonPhone): Promise<PersonPhoneStaff> {
    const { data } = await this.client.post<unknown>(`/personphone/staff`, serializeCbiDto(source, "generated.dto.CreatePersonPhone"));
    return deserializeCbiDto<PersonPhoneStaff>(data, "generated.dto.PersonPhoneStaff");
  }

  async updatePersonMember(source: UpdatePersonMember): Promise<PersonMember> {
    const { data } = await this.client.put<unknown>(`/person/member/${source.personId}`, serializeCbiDto(source, "generated.dto.UpdatePersonMember"));
    return deserializeCbiDto<PersonMember>(data, "generated.dto.PersonMember");
  }

  async updatePersonStaff(source: UpdatePersonStaff): Promise<PersonStaff> {
    const { data } = await this.client.put<unknown>(`/person/staff/${source.personId}`, serializeCbiDto(source, "generated.dto.UpdatePersonStaff"));
    return deserializeCbiDto<PersonStaff>(data, "generated.dto.PersonStaff");
  }

  async updatePersonPersonTypeStaff(source: UpdatePersonPersonType): Promise<PersonPersonTypeStaff> {
    const { data } = await this.client.put<unknown>(`/personpersontype/staff/${source.assignId}`, serializeCbiDto(source, "generated.dto.UpdatePersonPersonType"));
    return deserializeCbiDto<PersonPersonTypeStaff>(data, "generated.dto.PersonPersonTypeStaff");
  }

  async updatePersonRelationStaff(source: UpdatePersonRelation): Promise<PersonRelationStaff> {
    const { data } = await this.client.put<unknown>(`/personrelation/staff/${source.relationId}`, serializeCbiDto(source, "generated.dto.UpdatePersonRelation"));
    return deserializeCbiDto<PersonRelationStaff>(data, "generated.dto.PersonRelationStaff");
  }

  async updatePersonReferralStaff(source: UpdatePersonReferral): Promise<PersonReferralStaff> {
    const { data } = await this.client.put<unknown>(`/personreferral/staff/${source.assignId}`, serializeCbiDto(source, "generated.dto.UpdatePersonReferral"));
    return deserializeCbiDto<PersonReferralStaff>(data, "generated.dto.PersonReferralStaff");
  }

  async updatePersonAddressStaff(source: UpdatePersonAddress): Promise<PersonAddressStaff> {
    const { data } = await this.client.put<unknown>(`/personaddress/staff/${source.assignId}`, serializeCbiDto(source, "generated.dto.UpdatePersonAddress"));
    return deserializeCbiDto<PersonAddressStaff>(data, "generated.dto.PersonAddressStaff");
  }

  async updatePersonPhoneStaff(source: UpdatePersonPhone): Promise<PersonPhoneStaff> {
    const { data } = await this.client.put<unknown>(`/personphone/staff/${source.assignId}`, serializeCbiDto(source, "generated.dto.UpdatePersonPhone"));
    return deserializeCbiDto<PersonPhoneStaff>(data, "generated.dto.PersonPhoneStaff");
  }

  async deletePersonStaff(source: DeletePersonStaff): Promise<void> {
    await this.client.delete(`/person/staff/${source.personId}`);
  }

  async deletePersonPersonTypeStaff(source: DeletePersonPersonTypeStaff): Promise<void> {
    await this.client.delete(`/personpersontype/staff/${source.assignId}`);
  }

  async deletePersonRelationStaff(source: DeletePersonRelationStaff): Promise<void> {
    await this.client.delete(`/personrelation/staff/${source.relationId}`);
  }

  async deletePersonReferralStaff(source: DeletePersonReferralStaff): Promise<void> {
    await this.client.delete(`/personreferral/staff/${source.assignId}`);
  }

  async deletePersonAddressStaff(source: DeletePersonAddressStaff): Promise<void> {
    await this.client.delete(`/personaddress/staff/${source.assignId}`);
  }

  async deletePersonPhoneStaff(source: DeletePersonPhoneStaff): Promise<void> {
    await this.client.delete(`/personphone/staff/${source.assignId}`);
  }

  async searchPersonMember(params: PersonMemberSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonMemberSearchResult>> {
    const { data } = await this.client.post<unknown>(`/person/member/search`, serializeCbiDto(params, "generated.dto.PersonMemberSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<PersonMemberSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<PersonMemberSearchResult>>(result.items, "generated.dto.PersonMemberSearchResult") };
    return result;
  }

  async searchPersonStaff(params: PersonStaffSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/person/staff/search`, serializeCbiDto(params, "generated.dto.PersonStaffSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<PersonStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<PersonStaffSearchResult>>(result.items, "generated.dto.PersonStaffSearchResult") };
    return result;
  }

  async searchPersonRelationStaff(params: PersonRelationSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonRelationStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/personrelation/staff/search`, serializeCbiDto(params, "generated.dto.PersonRelationSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<PersonRelationStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<PersonRelationStaffSearchResult>>(result.items, "generated.dto.PersonRelationStaffSearchResult") };
    return result;
  }

  async searchPersonReferralStaff(params: PersonReferralSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonReferralStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/personreferral/staff/search`, serializeCbiDto(params, "generated.dto.PersonReferralSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<PersonReferralStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<PersonReferralStaffSearchResult>>(result.items, "generated.dto.PersonReferralStaffSearchResult") };
    return result;
  }

  async searchPersonAddressStaff(params: PersonAddressSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonAddressStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/personaddress/staff/search`, serializeCbiDto(params, "generated.dto.PersonAddressSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<PersonAddressStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<PersonAddressStaffSearchResult>>(result.items, "generated.dto.PersonAddressStaffSearchResult") };
    return result;
  }

  async searchPersonPhoneStaff(params: PersonPhoneSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonPhoneStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/personphone/staff/search`, serializeCbiDto(params, "generated.dto.PersonPhoneSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<PersonPhoneStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<PersonPhoneStaffSearchResult>>(result.items, "generated.dto.PersonPhoneStaffSearchResult") };
    return result;
  }

  async searchPersonCommentStaff(params: PersonCommentSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonCommentStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/personcomment/staff/search`, serializeCbiDto(params, "generated.dto.PersonCommentSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<PersonCommentStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<PersonCommentStaffSearchResult>>(result.items, "generated.dto.PersonCommentStaffSearchResult") };
    return result;
  }

  async searchPersonTagStaff(params: PersonTagSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonTagStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/persontag/staff/search`, serializeCbiDto(params, "generated.dto.PersonTagSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<PersonTagStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<PersonTagStaffSearchResult>>(result.items, "generated.dto.PersonTagStaffSearchResult") };
    return result;
  }

  async searchSkillStaff(params: SkillSearchParams, page: number, perPage: number): Promise<PaginationResult<SkillStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/skill/staff/search`, serializeCbiDto(params, "generated.dto.SkillSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<SkillStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<SkillStaffSearchResult>>(result.items, "generated.dto.SkillStaffSearchResult") };
    return result;
  }

  async searchPersonSkillStaff(params: PersonSkillSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonSkillStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/personskill/staff/search`, serializeCbiDto(params, "generated.dto.PersonSkillSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<PersonSkillStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<PersonSkillStaffSearchResult>>(result.items, "generated.dto.PersonSkillStaffSearchResult") };
    return result;
  }

  async searchPersonSkillChangeStaff(params: PersonSkillChangeSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonSkillChangeStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/personskillchange/staff/search`, serializeCbiDto(params, "generated.dto.PersonSkillChangeSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<PersonSkillChangeStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<PersonSkillChangeStaffSearchResult>>(result.items, "generated.dto.PersonSkillChangeStaffSearchResult") };
    return result;
  }

  async searchRatingChangeStaff(params: RatingChangeSearchParams, page: number, perPage: number): Promise<PaginationResult<RatingChangeStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/ratingchange/staff/search`, serializeCbiDto(params, "generated.dto.RatingChangeSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<RatingChangeStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<RatingChangeStaffSearchResult>>(result.items, "generated.dto.RatingChangeStaffSearchResult") };
    return result;
  }

  async searchPersonAgreementInstanceStaff(params: PersonAgreementInstanceSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonAgreementInstanceStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/personagreementinstance/staff/search`, serializeCbiDto(params, "generated.dto.PersonAgreementInstanceSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<PersonAgreementInstanceStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<PersonAgreementInstanceStaffSearchResult>>(result.items, "generated.dto.PersonAgreementInstanceStaffSearchResult") };
    return result;
  }

  async searchSignoutMember(params: SignoutMemberSearchParams, page: number, perPage: number): Promise<PaginationResult<SignoutMemberSearchResult>> {
    const { data } = await this.client.post<unknown>(`/signout/member/search`, serializeCbiDto(params, "generated.dto.SignoutMemberSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<SignoutMemberSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<SignoutMemberSearchResult>>(result.items, "generated.dto.SignoutMemberSearchResult") };
    return result;
  }

  async searchSignoutStaff(params: SignoutSearchParams, page: number, perPage: number): Promise<PaginationResult<SignoutStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/signout/staff/search`, serializeCbiDto(params, "generated.dto.SignoutSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<SignoutStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<SignoutStaffSearchResult>>(result.items, "generated.dto.SignoutStaffSearchResult") };
    return result;
  }

  async searchSignoutCrewStaff(params: SignoutCrewSearchParams, page: number, perPage: number): Promise<PaginationResult<SignoutCrewStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/signoutcrew/staff/search`, serializeCbiDto(params, "generated.dto.SignoutCrewSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<SignoutCrewStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<SignoutCrewStaffSearchResult>>(result.items, "generated.dto.SignoutCrewStaffSearchResult") };
    return result;
  }

  async searchPersonMemberFull(params: PersonMemberSearchParams, page: number, perPage: number, options?: PersonMemberFetchOptions): Promise<PaginationResult<PersonMember>> {
    const { data } = await this.client.post<unknown>(`/person/member/search/full`, serializeCbiDto(params, "generated.dto.PersonMemberSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.PersonMemberFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<PersonMember>;
    result = { ...result, items: deserializeCbiDto<Array<PersonMember>>(result.items, "generated.dto.PersonMember") };
    return result;
  }

  async searchPersonStaffFull(params: PersonStaffSearchParams, page: number, perPage: number, options?: PersonFetchOptions): Promise<PaginationResult<PersonStaff>> {
    const { data } = await this.client.post<unknown>(`/person/staff/search/full`, serializeCbiDto(params, "generated.dto.PersonStaffSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.PersonFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<PersonStaff>;
    result = { ...result, items: deserializeCbiDto<Array<PersonStaff>>(result.items, "generated.dto.PersonStaff") };
    return result;
  }

  async searchPersonRelationStaffFull(params: PersonRelationSearchParams, page: number, perPage: number, options?: PersonRelationFetchOptions): Promise<PaginationResult<PersonRelationStaff>> {
    const { data } = await this.client.post<unknown>(`/personrelation/staff/search/full`, serializeCbiDto(params, "generated.dto.PersonRelationSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.PersonRelationFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<PersonRelationStaff>;
    result = { ...result, items: deserializeCbiDto<Array<PersonRelationStaff>>(result.items, "generated.dto.PersonRelationStaff") };
    return result;
  }

  async searchPersonReferralStaffFull(params: PersonReferralSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonReferralStaff>> {
    const { data } = await this.client.post<unknown>(`/personreferral/staff/search/full`, serializeCbiDto(params, "generated.dto.PersonReferralSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<PersonReferralStaff>;
    result = { ...result, items: deserializeCbiDto<Array<PersonReferralStaff>>(result.items, "generated.dto.PersonReferralStaff") };
    return result;
  }

  async searchPersonAddressStaffFull(params: PersonAddressSearchParams, page: number, perPage: number, options?: PersonAddressFetchOptions): Promise<PaginationResult<PersonAddressStaff>> {
    const { data } = await this.client.post<unknown>(`/personaddress/staff/search/full`, serializeCbiDto(params, "generated.dto.PersonAddressSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.PersonAddressFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<PersonAddressStaff>;
    result = { ...result, items: deserializeCbiDto<Array<PersonAddressStaff>>(result.items, "generated.dto.PersonAddressStaff") };
    return result;
  }

  async searchPersonPhoneStaffFull(params: PersonPhoneSearchParams, page: number, perPage: number, options?: PersonPhoneFetchOptions): Promise<PaginationResult<PersonPhoneStaff>> {
    const { data } = await this.client.post<unknown>(`/personphone/staff/search/full`, serializeCbiDto(params, "generated.dto.PersonPhoneSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.PersonPhoneFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<PersonPhoneStaff>;
    result = { ...result, items: deserializeCbiDto<Array<PersonPhoneStaff>>(result.items, "generated.dto.PersonPhoneStaff") };
    return result;
  }

  async searchPersonCommentStaffFull(params: PersonCommentSearchParams, page: number, perPage: number, options?: PersonCommentFetchOptions): Promise<PaginationResult<PersonCommentStaff>> {
    const { data } = await this.client.post<unknown>(`/personcomment/staff/search/full`, serializeCbiDto(params, "generated.dto.PersonCommentSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.PersonCommentFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<PersonCommentStaff>;
    result = { ...result, items: deserializeCbiDto<Array<PersonCommentStaff>>(result.items, "generated.dto.PersonCommentStaff") };
    return result;
  }

  async searchPersonTagStaffFull(params: PersonTagSearchParams, page: number, perPage: number, options?: PersonTagFetchOptions): Promise<PaginationResult<PersonTagStaff>> {
    const { data } = await this.client.post<unknown>(`/persontag/staff/search/full`, serializeCbiDto(params, "generated.dto.PersonTagSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.PersonTagFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<PersonTagStaff>;
    result = { ...result, items: deserializeCbiDto<Array<PersonTagStaff>>(result.items, "generated.dto.PersonTagStaff") };
    return result;
  }

  async searchSkillStaffFull(params: SkillSearchParams, page: number, perPage: number): Promise<PaginationResult<SkillStaff>> {
    const { data } = await this.client.post<unknown>(`/skill/staff/search/full`, serializeCbiDto(params, "generated.dto.SkillSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<SkillStaff>;
    result = { ...result, items: deserializeCbiDto<Array<SkillStaff>>(result.items, "generated.dto.SkillStaff") };
    return result;
  }

  async searchPersonSkillStaffFull(params: PersonSkillSearchParams, page: number, perPage: number, options?: PersonSkillFetchOptions): Promise<PaginationResult<PersonSkillStaff>> {
    const { data } = await this.client.post<unknown>(`/personskill/staff/search/full`, serializeCbiDto(params, "generated.dto.PersonSkillSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.PersonSkillFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<PersonSkillStaff>;
    result = { ...result, items: deserializeCbiDto<Array<PersonSkillStaff>>(result.items, "generated.dto.PersonSkillStaff") };
    return result;
  }

  async searchPersonSkillChangeStaffFull(params: PersonSkillChangeSearchParams, page: number, perPage: number, options?: PersonSkillChangeFetchOptions): Promise<PaginationResult<PersonSkillChangeStaff>> {
    const { data } = await this.client.post<unknown>(`/personskillchange/staff/search/full`, serializeCbiDto(params, "generated.dto.PersonSkillChangeSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.PersonSkillChangeFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<PersonSkillChangeStaff>;
    result = { ...result, items: deserializeCbiDto<Array<PersonSkillChangeStaff>>(result.items, "generated.dto.PersonSkillChangeStaff") };
    return result;
  }

  async searchRatingChangeStaffFull(params: RatingChangeSearchParams, page: number, perPage: number, options?: RatingChangeFetchOptions): Promise<PaginationResult<RatingChangeStaff>> {
    const { data } = await this.client.post<unknown>(`/ratingchange/staff/search/full`, serializeCbiDto(params, "generated.dto.RatingChangeSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.RatingChangeFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<RatingChangeStaff>;
    result = { ...result, items: deserializeCbiDto<Array<RatingChangeStaff>>(result.items, "generated.dto.RatingChangeStaff") };
    return result;
  }

  async searchPersonAgreementInstanceStaffFull(params: PersonAgreementInstanceSearchParams, page: number, perPage: number, options?: PersonAgreementInstanceFetchOptions): Promise<PaginationResult<PersonAgreementInstanceStaff>> {
    const { data } = await this.client.post<unknown>(`/personagreementinstance/staff/search/full`, serializeCbiDto(params, "generated.dto.PersonAgreementInstanceSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.PersonAgreementInstanceFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<PersonAgreementInstanceStaff>;
    result = { ...result, items: deserializeCbiDto<Array<PersonAgreementInstanceStaff>>(result.items, "generated.dto.PersonAgreementInstanceStaff") };
    return result;
  }

  async searchSignoutMemberFull(params: SignoutMemberSearchParams, page: number, perPage: number, options?: SignoutMemberFetchOptions): Promise<PaginationResult<SignoutMember>> {
    const { data } = await this.client.post<unknown>(`/signout/member/search/full`, serializeCbiDto(params, "generated.dto.SignoutMemberSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.SignoutMemberFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<SignoutMember>;
    result = { ...result, items: deserializeCbiDto<Array<SignoutMember>>(result.items, "generated.dto.SignoutMember") };
    return result;
  }

  async searchSignoutStaffFull(params: SignoutSearchParams, page: number, perPage: number, options?: SignoutFetchOptions): Promise<PaginationResult<SignoutStaff>> {
    const { data } = await this.client.post<unknown>(`/signout/staff/search/full`, serializeCbiDto(params, "generated.dto.SignoutSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.SignoutFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<SignoutStaff>;
    result = { ...result, items: deserializeCbiDto<Array<SignoutStaff>>(result.items, "generated.dto.SignoutStaff") };
    return result;
  }

  async searchSignoutCrewStaffFull(params: SignoutCrewSearchParams, page: number, perPage: number, options?: SignoutCrewFetchOptions): Promise<PaginationResult<SignoutCrewStaff>> {
    const { data } = await this.client.post<unknown>(`/signoutcrew/staff/search/full`, serializeCbiDto(params, "generated.dto.SignoutCrewSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.SignoutCrewFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<SignoutCrewStaff>;
    result = { ...result, items: deserializeCbiDto<Array<SignoutCrewStaff>>(result.items, "generated.dto.SignoutCrewStaff") };
    return result;
  }

  async getPersonMemberMemberships(id: number, options?: MembershipMemberFetchOptions): Promise<Array<MembershipMember>> {
    const { data } = await this.client.get<unknown>(`/person/member/${id}/memberships`, { params: serializeCbiDto(options, "generated.dto.MembershipMemberFetchOptions") });
    return deserializeCbiDto<Array<MembershipMember>>(data, "generated.dto.MembershipMember");
  }

  async getPersonStaffReferrals(id: number): Promise<Array<PersonReferralStaff>> {
    const { data } = await this.client.get<unknown>(`/person/staff/${id}/referrals`);
    return deserializeCbiDto<Array<PersonReferralStaff>>(data, "generated.dto.PersonReferralStaff");
  }

  async getPersonStaffDamageWaivers(id: number, options?: DamageWaiverFetchOptions): Promise<Array<DamageWaiverStaff>> {
    const { data } = await this.client.get<unknown>(`/person/staff/${id}/damagewaivers`, { params: serializeCbiDto(options, "generated.dto.DamageWaiverFetchOptions") });
    return deserializeCbiDto<Array<DamageWaiverStaff>>(data, "generated.dto.DamageWaiverStaff");
  }

  async getPersonStaffPhones(id: number, options?: PersonPhoneFetchOptions): Promise<Array<PersonPhoneStaff>> {
    const { data } = await this.client.get<unknown>(`/person/staff/${id}/phones`, { params: serializeCbiDto(options, "generated.dto.PersonPhoneFetchOptions") });
    return deserializeCbiDto<Array<PersonPhoneStaff>>(data, "generated.dto.PersonPhoneStaff");
  }

  async getPersonStaffPersonRatings(id: number, options?: PersonRatingFetchOptions): Promise<Array<PersonRatingStaff>> {
    const { data } = await this.client.get<unknown>(`/person/staff/${id}/personratings`, { params: serializeCbiDto(options, "generated.dto.PersonRatingFetchOptions") });
    return deserializeCbiDto<Array<PersonRatingStaff>>(data, "generated.dto.PersonRatingStaff");
  }

  async getPersonStaffMemberships(id: number, options?: MembershipStaffFetchOptions): Promise<Array<MembershipStaff>> {
    const { data } = await this.client.get<unknown>(`/person/staff/${id}/memberships`, { params: serializeCbiDto(options, "generated.dto.MembershipStaffFetchOptions") });
    return deserializeCbiDto<Array<MembershipStaff>>(data, "generated.dto.MembershipStaff");
  }

  async getPersonStaffAddresses(id: number, options?: PersonAddressFetchOptions): Promise<Array<PersonAddressStaff>> {
    const { data } = await this.client.get<unknown>(`/person/staff/${id}/addresses`, { params: serializeCbiDto(options, "generated.dto.PersonAddressFetchOptions") });
    return deserializeCbiDto<Array<PersonAddressStaff>>(data, "generated.dto.PersonAddressStaff");
  }

  async getPersonStaffCards(id: number, options?: CardFetchOptions): Promise<Array<CardStaff>> {
    const { data } = await this.client.get<unknown>(`/person/staff/${id}/cards`, { params: serializeCbiDto(options, "generated.dto.CardFetchOptions") });
    return deserializeCbiDto<Array<CardStaff>>(data, "generated.dto.CardStaff");
  }

  async getPersonStaffPersonTypes(id: number, options?: PersonPersonTypeFetchOptions): Promise<Array<PersonPersonTypeStaff>> {
    const { data } = await this.client.get<unknown>(`/person/staff/${id}/persontypes`, { params: serializeCbiDto(options, "generated.dto.PersonPersonTypeFetchOptions") });
    return deserializeCbiDto<Array<PersonPersonTypeStaff>>(data, "generated.dto.PersonPersonTypeStaff");
  }

  async getPersonStaffApClassesInstructing(id: number, options?: ApClassFetchOptions): Promise<Array<ApClassStaff>> {
    const { data } = await this.client.get<unknown>(`/person/staff/${id}/apclassesinstructing`, { params: serializeCbiDto(options, "generated.dto.ApClassFetchOptions") });
    return deserializeCbiDto<Array<ApClassStaff>>(data, "generated.dto.ApClassStaff");
  }

  async getPersonStaffJpClassesInstructing(id: number, options?: JpClassFetchOptions): Promise<Array<JpClassStaff>> {
    const { data } = await this.client.get<unknown>(`/person/staff/${id}/jpclassesinstructing`, { params: serializeCbiDto(options, "generated.dto.JpClassFetchOptions") });
    return deserializeCbiDto<Array<JpClassStaff>>(data, "generated.dto.JpClassStaff");
  }

  async getPersonStaffApClassSignups(id: number, options?: ApClassSignupStaffFetchOptions): Promise<Array<ApClassSignupStaff>> {
    const { data } = await this.client.get<unknown>(`/person/staff/${id}/apclasssignups`, { params: serializeCbiDto(options, "generated.dto.ApClassSignupStaffFetchOptions") });
    return deserializeCbiDto<Array<ApClassSignupStaff>>(data, "generated.dto.ApClassSignupStaff");
  }

  async getPersonStaffJpClassSignups(id: number, options?: JpClassSignupFetchOptions): Promise<Array<JpClassSignupStaff>> {
    const { data } = await this.client.get<unknown>(`/person/staff/${id}/jpclasssignups`, { params: serializeCbiDto(options, "generated.dto.JpClassSignupFetchOptions") });
    return deserializeCbiDto<Array<JpClassSignupStaff>>(data, "generated.dto.JpClassSignupStaff");
  }

  async getPersonStaffApClassTypeOverrides(id: number, options?: ApClassTypeOverrideFetchOptions): Promise<Array<ApClassTypeOverrideStaff>> {
    const { data } = await this.client.get<unknown>(`/person/staff/${id}/apclasstypeoverrides`, { params: serializeCbiDto(options, "generated.dto.ApClassTypeOverrideFetchOptions") });
    return deserializeCbiDto<Array<ApClassTypeOverrideStaff>>(data, "generated.dto.ApClassTypeOverrideStaff");
  }

  async getPersonStaffJpClassTypeOverrides(id: number, options?: JpClassTypeOverrideFetchOptions): Promise<Array<JpClassTypeOverrideStaff>> {
    const { data } = await this.client.get<unknown>(`/person/staff/${id}/jpclasstypeoverrides`, { params: serializeCbiDto(options, "generated.dto.JpClassTypeOverrideFetchOptions") });
    return deserializeCbiDto<Array<JpClassTypeOverrideStaff>>(data, "generated.dto.JpClassTypeOverrideStaff");
  }

  async getPersonStaffRelationsA(id: number, options?: PersonRelationFetchOptions): Promise<Array<PersonRelationStaff>> {
    const { data } = await this.client.get<unknown>(`/person/staff/${id}/relationsa`, { params: serializeCbiDto(options, "generated.dto.PersonRelationFetchOptions") });
    return deserializeCbiDto<Array<PersonRelationStaff>>(data, "generated.dto.PersonRelationStaff");
  }

  async getPersonStaffRelationsB(id: number, options?: PersonRelationFetchOptions): Promise<Array<PersonRelationStaff>> {
    const { data } = await this.client.get<unknown>(`/person/staff/${id}/relationsb`, { params: serializeCbiDto(options, "generated.dto.PersonRelationFetchOptions") });
    return deserializeCbiDto<Array<PersonRelationStaff>>(data, "generated.dto.PersonRelationStaff");
  }

  async getPersonStaffCheckIns(id: number, options?: PersonCheckInFetchOptions): Promise<Array<PersonCheckInStaff>> {
    const { data } = await this.client.get<unknown>(`/person/staff/${id}/checkins`, { params: serializeCbiDto(options, "generated.dto.PersonCheckInFetchOptions") });
    return deserializeCbiDto<Array<PersonCheckInStaff>>(data, "generated.dto.PersonCheckInStaff");
  }

  async getPersonPersonTypeStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    const { data } = await this.client.get<unknown>(`/personpersontype/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff>(data, "generated.dto.PersonStaff");
  }

  async getPersonPersonTypeStaffTypeId(id: number): Promise<PersonTypeStaff> {
    const { data } = await this.client.get<unknown>(`/personpersontype/staff/${id}/typeid`);
    return deserializeCbiDto<PersonTypeStaff>(data, "generated.dto.PersonTypeStaff");
  }

  async getPersonRelationStaffAPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    const { data } = await this.client.get<unknown>(`/personrelation/staff/${id}/apersonid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff | null>(data, "generated.dto.PersonStaff");
  }

  async getPersonRelationStaffBPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    const { data } = await this.client.get<unknown>(`/personrelation/staff/${id}/bpersonid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff | null>(data, "generated.dto.PersonStaff");
  }

  async getPersonAddressStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    const { data } = await this.client.get<unknown>(`/personaddress/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff>(data, "generated.dto.PersonStaff");
  }

  async getPersonAddressStaffAddressId(id: number): Promise<AddressStaff> {
    const { data } = await this.client.get<unknown>(`/personaddress/staff/${id}/addressid`);
    return deserializeCbiDto<AddressStaff>(data, "generated.dto.AddressStaff");
  }

  async getPersonPhoneStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    const { data } = await this.client.get<unknown>(`/personphone/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff>(data, "generated.dto.PersonStaff");
  }

  async getPersonPhoneStaffPhoneId(id: number): Promise<PhoneStaff> {
    const { data } = await this.client.get<unknown>(`/personphone/staff/${id}/phoneid`);
    return deserializeCbiDto<PhoneStaff>(data, "generated.dto.PhoneStaff");
  }

  async getPersonCommentStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    const { data } = await this.client.get<unknown>(`/personcomment/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff>(data, "generated.dto.PersonStaff");
  }

  async getPersonTagStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    const { data } = await this.client.get<unknown>(`/persontag/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff | null>(data, "generated.dto.PersonStaff");
  }

  async getPersonTagStaffTagId(id: number): Promise<TagStaff | null> {
    const { data } = await this.client.get<unknown>(`/persontag/staff/${id}/tagid`);
    return deserializeCbiDto<TagStaff | null>(data, "generated.dto.TagStaff");
  }

  async getSkillAssessmentStaffSkillId(id: number): Promise<SkillStaff> {
    const { data } = await this.client.get<unknown>(`/skillassessment/staff/${id}/skillid`);
    return deserializeCbiDto<SkillStaff>(data, "generated.dto.SkillStaff");
  }

  async getSkillAssessmentStaffAssessmentId(id: number): Promise<AssessmentStaff> {
    const { data } = await this.client.get<unknown>(`/skillassessment/staff/${id}/assessmentid`);
    return deserializeCbiDto<AssessmentStaff>(data, "generated.dto.AssessmentStaff");
  }

  async getPersonSkillStaffSkillId(id: number): Promise<SkillStaff> {
    const { data } = await this.client.get<unknown>(`/personskill/staff/${id}/skillid`);
    return deserializeCbiDto<SkillStaff>(data, "generated.dto.SkillStaff");
  }

  async getPersonSkillStaffAssessmentId(id: number): Promise<AssessmentStaff> {
    const { data } = await this.client.get<unknown>(`/personskill/staff/${id}/assessmentid`);
    return deserializeCbiDto<AssessmentStaff>(data, "generated.dto.AssessmentStaff");
  }

  async getPersonSkillStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    const { data } = await this.client.get<unknown>(`/personskill/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff>(data, "generated.dto.PersonStaff");
  }

  async getPersonSkillChangeStaffAssignId(id: number, options?: PersonSkillFetchOptions): Promise<PersonSkillStaff> {
    const { data } = await this.client.get<unknown>(`/personskillchange/staff/${id}/assignid`, { params: serializeCbiDto(options, "generated.dto.PersonSkillFetchOptions") });
    return deserializeCbiDto<PersonSkillStaff>(data, "generated.dto.PersonSkillStaff");
  }

  async getPersonSkillChangeStaffAssessmentId(id: number): Promise<AssessmentStaff | null> {
    const { data } = await this.client.get<unknown>(`/personskillchange/staff/${id}/assessmentid`);
    return deserializeCbiDto<AssessmentStaff | null>(data, "generated.dto.AssessmentStaff");
  }

  async getPersonSkillChangeStaffSkillId(id: number): Promise<SkillStaff> {
    const { data } = await this.client.get<unknown>(`/personskillchange/staff/${id}/skillid`);
    return deserializeCbiDto<SkillStaff>(data, "generated.dto.SkillStaff");
  }

  async getPersonSkillChangeStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    const { data } = await this.client.get<unknown>(`/personskillchange/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff>(data, "generated.dto.PersonStaff");
  }

  async getRatingChangeStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    const { data } = await this.client.get<unknown>(`/ratingchange/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff | null>(data, "generated.dto.PersonStaff");
  }

  async getRatingChangeStaffRatingId(id: number, options?: RatingFetchOptions): Promise<RatingStaff | null> {
    const { data } = await this.client.get<unknown>(`/ratingchange/staff/${id}/ratingid`, { params: serializeCbiDto(options, "generated.dto.RatingFetchOptions") });
    return deserializeCbiDto<RatingStaff | null>(data, "generated.dto.RatingStaff");
  }

  async getRatingChangeStaffProgramId(id: number): Promise<ProgramTypeStaff | null> {
    const { data } = await this.client.get<unknown>(`/ratingchange/staff/${id}/programid`);
    return deserializeCbiDto<ProgramTypeStaff | null>(data, "generated.dto.ProgramTypeStaff");
  }

  async getPersonAgreementInstanceStaffAgreementInstanceId(id: number, options?: AgreementInstanceFetchOptions): Promise<AgreementInstanceStaff> {
    const { data } = await this.client.get<unknown>(`/personagreementinstance/staff/${id}/agreementinstanceid`, { params: serializeCbiDto(options, "generated.dto.AgreementInstanceFetchOptions") });
    return deserializeCbiDto<AgreementInstanceStaff>(data, "generated.dto.AgreementInstanceStaff");
  }

  async getPersonAgreementInstanceStaffAgreementId(id: number, options?: AgreementFetchOptions): Promise<AgreementStaff | null> {
    const { data } = await this.client.get<unknown>(`/personagreementinstance/staff/${id}/agreementid`, { params: serializeCbiDto(options, "generated.dto.AgreementFetchOptions") });
    return deserializeCbiDto<AgreementStaff | null>(data, "generated.dto.AgreementStaff");
  }

  async getPersonAgreementInstanceStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    const { data } = await this.client.get<unknown>(`/personagreementinstance/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff>(data, "generated.dto.PersonStaff");
  }

  async getSignoutStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    const { data } = await this.client.get<unknown>(`/signout/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff | null>(data, "generated.dto.PersonStaff");
  }

  async getSignoutStaffProgramId(id: number): Promise<ProgramTypeStaff> {
    const { data } = await this.client.get<unknown>(`/signout/staff/${id}/programid`);
    return deserializeCbiDto<ProgramTypeStaff>(data, "generated.dto.ProgramTypeStaff");
  }

  async getSignoutStaffBoatId(id: number): Promise<BoatTypeStaff> {
    const { data } = await this.client.get<unknown>(`/signout/staff/${id}/boatid`);
    return deserializeCbiDto<BoatTypeStaff>(data, "generated.dto.BoatTypeStaff");
  }

  async getSignoutStaffTestRatingId(id: number, options?: RatingFetchOptions): Promise<RatingStaff | null> {
    const { data } = await this.client.get<unknown>(`/signout/staff/${id}/testratingid`, { params: serializeCbiDto(options, "generated.dto.RatingFetchOptions") });
    return deserializeCbiDto<RatingStaff | null>(data, "generated.dto.RatingStaff");
  }

  async getSignoutCrewStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    const { data } = await this.client.get<unknown>(`/signoutcrew/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff | null>(data, "generated.dto.PersonStaff");
  }

  async getSignoutCrewStaffSignoutId(id: number, options?: SignoutFetchOptions): Promise<SignoutStaff | null> {
    const { data } = await this.client.get<unknown>(`/signoutcrew/staff/${id}/signoutid`, { params: serializeCbiDto(options, "generated.dto.SignoutFetchOptions") });
    return deserializeCbiDto<SignoutStaff | null>(data, "generated.dto.SignoutStaff");
  }

  async createChildMember(request: MemberProfileCreate): Promise<MemberProfile> {
    const { data } = await this.client.post<unknown>(`/person/member/createchild`, serializeCbiDto(request, "generated.dto.MemberProfileCreate"));
    return deserializeCbiDto<MemberProfile>(data, "generated.dto.MemberProfile");
  }

  async createEmergencyContactMember(request: PersonCreateEmergencyContactRequest): Promise<MemberProfile> {
    const { data } = await this.client.post<unknown>(`/person/member/createemergencycontact`, serializeCbiDto(request, "generated.dto.PersonCreateEmergencyContactRequest"));
    return deserializeCbiDto<MemberProfile>(data, "generated.dto.MemberProfile");
  }

  async createStaffProfileStaff(request: StaffProfileCreateRequest): Promise<PersonStaff> {
    const { data } = await this.client.post<unknown>(`/person/staff/createstaffprofile`, serializeCbiDto(request, "generated.dto.StaffProfileCreateRequest"));
    let result = data as PersonStaff;
    result = deserializeCbiDto<PersonStaff>(result, "generated.dto.PersonStaff");
    return result;
  }

  async currentPersonMember(): Promise<PersonMember> {
    const { data } = await this.client.get<unknown>(`/person/member/currentperson`);
    let result = data as PersonMember;
    result = deserializeCbiDto<PersonMember>(result, "generated.dto.PersonMember");
    return result;
  }

  async findPersonByNameStaff(name: string): Promise<PersonStaff> {
    const { data } = await this.client.post<unknown>(`/person/staff/findpersonbyname`, JSON.stringify(name));
    let result = data as PersonStaff;
    result = deserializeCbiDto<PersonStaff>(result, "generated.dto.PersonStaff");
    return result;
  }

  async getChildrenStaff(ref: PersonRef): Promise<Array<PersonStaff>> {
    const { data } = await this.client.get<unknown>(`/person/staff/getchildren`, { params: { ref: encodeRequestParam(ref, "generated.dto.PersonRef") } });
    let result = data as Array<PersonStaff>;
    result = deserializeCbiDto<Array<PersonStaff>>(result, "generated.dto.PersonStaff");
    return result;
  }

  async getChildrenMember(ref: PersonRef): Promise<Array<PersonMember>> {
    const { data } = await this.client.get<unknown>(`/person/member/getchildren`, { params: { ref: encodeRequestParam(ref, "generated.dto.PersonRef") } });
    let result = data as Array<PersonMember>;
    result = deserializeCbiDto<Array<PersonMember>>(result, "generated.dto.PersonMember");
    return result;
  }

  async getEmergencyContactsStaff(ref: PersonRef): Promise<Array<PersonStaff>> {
    const { data } = await this.client.get<unknown>(`/person/staff/getemergencycontacts`, { params: { ref: encodeRequestParam(ref, "generated.dto.PersonRef") } });
    let result = data as Array<PersonStaff>;
    result = deserializeCbiDto<Array<PersonStaff>>(result, "generated.dto.PersonStaff");
    return result;
  }

  async getEmergencyContactsMember(ref: PersonRef): Promise<Array<PersonMember>> {
    const { data } = await this.client.get<unknown>(`/person/member/getemergencycontacts`, { params: { ref: encodeRequestParam(ref, "generated.dto.PersonRef") } });
    let result = data as Array<PersonMember>;
    result = deserializeCbiDto<Array<PersonMember>>(result, "generated.dto.PersonMember");
    return result;
  }

  async getPersonNameStaff(request: PersonGetPersonNameRequest): Promise<string> {
    const { data } = await this.client.get<unknown>(`/person/staff/getpersonname`, { params: { request: encodeRequestParam(request, "generated.dto.PersonGetPersonNameRequest") } });
    return data as string;
  }

  async getProfileMember(person: PersonRef): Promise<MemberProfile> {
    const { data } = await this.client.get<unknown>(`/person/member/getprofile`, { params: { person: encodeRequestParam(person, "generated.dto.PersonRef") } });
    return deserializeCbiDto<MemberProfile>(data, "generated.dto.MemberProfile");
  }

  async grantRatingStaff(request: PersonGrantRatingRequest): Promise<void> {
    await this.client.post(`/person/staff/grantrating`, serializeCbiDto(request, "generated.dto.PersonGrantRatingRequest"));
  }

  async removeCommentStaff(request: PersonRemoveCommentRequest): Promise<void> {
    await this.client.post(`/person/staff/removecomment`, serializeCbiDto(request, "generated.dto.PersonRemoveCommentRequest"));
  }

  async removePersonAddressStaff(request: PersonRemovePersonAddressRequest): Promise<void> {
    await this.client.post(`/person/staff/removepersonaddress`, serializeCbiDto(request, "generated.dto.PersonRemovePersonAddressRequest"));
  }

  async removePersonPhoneStaff(request: PersonRemovePersonPhoneRequest): Promise<void> {
    await this.client.post(`/person/staff/removepersonphone`, serializeCbiDto(request, "generated.dto.PersonRemovePersonPhoneRequest"));
  }

  async removeSkillStaff(request: PersonRemoveSkillRequest): Promise<void> {
    await this.client.post(`/person/staff/removeskill`, serializeCbiDto(request, "generated.dto.PersonRemoveSkillRequest"));
  }

  async rescindRatingStaff(request: PersonRescindRatingRequest): Promise<void> {
    await this.client.post(`/person/staff/rescindrating`, serializeCbiDto(request, "generated.dto.PersonRescindRatingRequest"));
  }

  async saveCommentStaff(request: PersonSaveCommentRequest): Promise<PersonCommentStaff> {
    const { data } = await this.client.post<unknown>(`/person/staff/savecomment`, serializeCbiDto(request, "generated.dto.PersonSaveCommentRequest"));
    let result = data as PersonCommentStaff;
    result = deserializeCbiDto<PersonCommentStaff>(result, "generated.dto.PersonCommentStaff");
    return result;
  }

  async savePersonAddressStaff(request: SavePersonAddress): Promise<PersonAddressStaff> {
    const { data } = await this.client.post<unknown>(`/person/staff/savepersonaddress`, serializeCbiDto(request, "generated.dto.SavePersonAddress"));
    let result = data as PersonAddressStaff;
    result = deserializeCbiDto<PersonAddressStaff>(result, "generated.dto.PersonAddressStaff");
    return result;
  }

  async savePersonPhoneStaff(request: SavePersonPhone): Promise<PersonPhoneStaff> {
    const { data } = await this.client.post<unknown>(`/person/staff/savepersonphone`, serializeCbiDto(request, "generated.dto.SavePersonPhone"));
    let result = data as PersonPhoneStaff;
    result = deserializeCbiDto<PersonPhoneStaff>(result, "generated.dto.PersonPhoneStaff");
    return result;
  }

  async saveSkillStaff(request: PersonSaveSkillRequest): Promise<PersonSkillStaff> {
    const { data } = await this.client.post<unknown>(`/person/staff/saveskill`, serializeCbiDto(request, "generated.dto.PersonSaveSkillRequest"));
    let result = data as PersonSkillStaff;
    result = deserializeCbiDto<PersonSkillStaff>(result, "generated.dto.PersonSkillStaff");
    return result;
  }

  async searchPersonAgreementsStaff(request: PersonSearchPersonAgreementsRequest): Promise<PersonAgreementsPage> {
    const { data } = await this.client.post<unknown>(`/person/staff/searchpersonagreements`, serializeCbiDto(request, "generated.dto.PersonSearchPersonAgreementsRequest"));
    return deserializeCbiDto<PersonAgreementsPage>(data, "generated.dto.PersonAgreementsPage");
  }

  async setAdminHoldStaff(request: PersonSetAdminHoldRequest): Promise<void> {
    await this.client.post(`/person/staff/setadminhold`, serializeCbiDto(request, "generated.dto.PersonSetAdminHoldRequest"));
  }

  async setTagsStaff(request: PersonSetTagsRequest): Promise<void> {
    await this.client.post(`/person/staff/settags`, serializeCbiDto(request, "generated.dto.PersonSetTagsRequest"));
  }

  async signAgreementStaff(request: PersonSignAgreementRequest): Promise<PersonAgreementInstanceStaff> {
    const { data } = await this.client.post<unknown>(`/person/staff/signagreement`, serializeCbiDto(request, "generated.dto.PersonSignAgreementRequest"));
    let result = data as PersonAgreementInstanceStaff;
    result = deserializeCbiDto<PersonAgreementInstanceStaff>(result, "generated.dto.PersonAgreementInstanceStaff");
    return result;
  }

  async swapRelationDirectionStaff(relation: PersonRelationRef): Promise<PersonRelationStaff> {
    const { data } = await this.client.post<unknown>(`/person/staff/swaprelationdirection`, serializeCbiDto(relation, "generated.dto.PersonRelationRef"));
    let result = data as PersonRelationStaff;
    result = deserializeCbiDto<PersonRelationStaff>(result, "generated.dto.PersonRelationStaff");
    return result;
  }

  async updateProfileMember(request: MemberProfileUpdate): Promise<MemberProfile> {
    const { data } = await this.client.post<unknown>(`/person/member/updateprofile`, serializeCbiDto(request, "generated.dto.MemberProfileUpdate"));
    return deserializeCbiDto<MemberProfile>(data, "generated.dto.MemberProfile");
  }

  async updateStaffProfileStaff(request: StaffProfileUpdateRequest): Promise<PersonStaff> {
    const { data } = await this.client.post<unknown>(`/person/staff/updatestaffprofile`, serializeCbiDto(request, "generated.dto.StaffProfileUpdateRequest"));
    let result = data as PersonStaff;
    result = deserializeCbiDto<PersonStaff>(result, "generated.dto.PersonStaff");
    return result;
  }
}

export const usePersonApi = (): PersonApi => new PersonApiClient();
